export function toRoundedScore(value, fallback = 50) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }
  return Math.round(numericValue);
}

export function getChemistryGrade(score, magnetScore, mirrorScore, language = 'ko') {
  const value = Number(score || 0);
  if (String(language).startsWith('en')) {
    if (value >= 90) return { code: 'S+', label: 'Exceptional Match', rarity: 'Top-tier attraction and comfort', interpretation: 'The overall visual synergy is exceptionally strong.' };
    if (value >= 82) return { code: 'S', label: 'High Match', rarity: 'Strong visual synergy', interpretation: 'Attraction and structural comfort reinforce each other.' };
    if (value >= 72) return { code: 'A', label: 'Good Match', rarity: 'Well-balanced compatibility', interpretation: 'The two main chemistry signals form a positive balance.' };
    if (value >= 60) return { code: 'B', label: 'Balanced Match', rarity: 'A mix of similarity and difference', interpretation: 'Chemistry can grow as both impressions become familiar.' };
    return { code: 'C', label: 'Exploratory Match', rarity: 'Distinct visual signals', interpretation: 'Both impressions stay distinct, so attraction may build gradually.' };
  }
  const magnet = Number(magnetScore || 0);
  const mirror = Number(mirrorScore || 0);

  if (value >= 90) {
    return {
      code: 'S+',
      label: '매우 높은 궁합',
      rarity: magnet >= 80 && mirror >= 70 ? '끌림과 안정감이 함께 높은 최고 등급' : '종합 시너지가 두드러지는 최고 등급',
      interpretation: magnet >= mirror
        ? '강한 시각적 끌림이 전체 궁합을 크게 끌어올린 결과입니다.'
        : '높은 구조적 안정감이 전체 궁합을 단단하게 만든 결과입니다.',
    };
  }
  if (value >= 82) {
    return {
      code: 'S',
      label: '높은 궁합',
      rarity: magnet >= mirror ? '시각적 시너지가 강한 상위 등급' : '안정적 조화가 돋보이는 상위 등급',
      interpretation: magnet >= mirror
        ? '서로 다른 인상에서 오는 시각적 긴장감이 강하게 작용한 결과입니다.'
        : '닮은 구조에서 오는 편안함과 조화가 높게 반영된 결과입니다.',
    };
  }
  if (value >= 72) {
    return {
      code: 'A',
      label: '좋은 궁합',
      rarity: Math.abs(magnet - mirror) <= 8 ? '끌림과 안정감이 고른 조화 등급' : '한 가지 강점이 선명한 조화 등급',
      interpretation: Math.abs(magnet - mirror) <= 8
        ? '시각적 끌림과 구조적 편안함이 균형 있게 형성된 결과입니다.'
        : '두 핵심 지표 중 한쪽의 뚜렷한 강점이 궁합을 이끄는 결과입니다.',
    };
  }
  if (value >= 60) {
    return {
      code: 'B',
      label: '균형 궁합',
      rarity: '차이와 유사성을 함께 살펴볼 균형 등급',
      interpretation: magnet >= mirror
        ? '시각적 차이의 에너지가 편안함보다 조금 더 크게 반영된 결과입니다.'
        : '구조적 편안함이 시각적 긴장감보다 조금 더 크게 반영된 결과입니다.',
    };
  }
  return {
    code: 'C',
    label: '천천히 확인할 궁합',
    rarity: '서로 다른 시각적 신호가 뚜렷한 탐색 등급',
    interpretation: '두 사람의 인상이 각자 강하게 유지되어 차이를 천천히 읽어볼 결과입니다.',
  };
}

export function normalizeDirectionFlow(resultOrFlow) {
  const flow = resultOrFlow?.attraction_flow ||
    resultOrFlow?.attention_flow ||
    resultOrFlow?.directionality ||
    resultOrFlow ||
    {};
  const aToB = toRoundedScore(flow?.a_to_b, 50);
  const bToA = toRoundedScore(flow?.b_to_a, 50);
  return {
    ...flow,
    a_to_b: aToB,
    b_to_a: bToA,
    delta: toRoundedScore(flow?.delta ?? aToB - bToA, 0),
    label: flow?.label || 'balanced',
  };
}

export function getAttentionLead(resultOrFlow) {
  const flow = normalizeDirectionFlow(resultOrFlow);
  const aScore = Number(flow.a_to_b ?? 50);
  const bScore = Number(flow.b_to_a ?? 50);
  const isBalanced = Math.abs(aScore - bScore) < 10 || (aScore < 55 && bScore < 55);

  if (isBalanced) return 'balanced';
  return aScore > bScore ? 'a' : 'b';
}

export function getDirectionSummary(resultOrFlow, nameA = 'A', nameB = 'B') {
  const flow = normalizeDirectionFlow(resultOrFlow);
  const lead = getAttentionLead(flow);

  if (lead === 'balanced') {
    return {
      flow,
      lead: 'balanced',
      tone: 'balanced',
      leadName: null,
      targetName: null,
      titleKey: 'balanced',
      nameA,
      nameB,
    };
  }

  return {
    flow,
    lead,
    tone: lead === 'a' ? 'pink' : 'blue',
    leadName: lead === 'a' ? nameA : nameB,
    targetName: lead === 'a' ? nameB : nameA,
    titleKey: lead === 'a' ? 'a_leads' : 'b_leads',
    nameA,
    nameB,
  };
}
